import * as React from 'react';
import classnames from 'classnames';
import { isBoolean, isEmpty, isEqual } from 'lodash';

import MixedSelectDropdownControl from './control/MixedSelectDropdownControl';
import MixedSelectDropdownMenuContainer from './menu/container/MixedSelectDropdownMenuContainer';

import { logError } from 'utils/log';
import { __t, applyTranslations } from 'utils/language';
import { expandDataProps } from 'utils/helper/common';
import {
  deselectAllItems,
  filterItems,
  getRemovedItem,
  getSelectedItems,
  getUnselectedItems,
  getUpdatedItem,
  removeItemFromList,
  selectAllItems,
  sortItems,
  updatePropValueByIdentifier
} from './utils/mixed-select-dropdown';

import './mixed-select-dropdown.scss';

applyTranslations(require('./mixed-select-dropdown.i18n.json'));

import type {
  Data,
  MixedSelectDropdownItem,
  MixedSelectDropdownItemState,
  TextEntry
} from 'types';

type Props = {
  name: string,
  items: Array<MixedSelectDropdownItem>,
  withSavedInput?: boolean,
  withMulti?: boolean,
  withSearchable?: boolean,
  withCloseOnSelect?: boolean,
  withRemovable?: boolean,
  withSortable?: boolean,
  withCheckboxes?: boolean,
  withColors?: boolean,
  withIcons?: boolean,
  withTruncate?: boolean,
  withSelectAll?: boolean,
  withDynamicData?: boolean,
  withSelectedInList?: boolean,
  withSmallSize?: boolean,
  withTags?: boolean,
  // to cover edgecase with Chrome when it decides to keep showing autocomplete even when it is disabled
  withRandomizedAutocomplete?: boolean,
  hideChevron?: boolean,
  hideSelectedItems?: boolean,
  className?: string,
  controlClassName?: string,
  inputClassName?: string,
  menuClassName?: string,
  menuItemClassName?: string,
  maxItems?: number,
  data?: Data,
  placeholder?: string,
  isDisabled?: boolean,
  onItemsSearch?: (a: string) => void,
  onItemsToggle?: (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ) => void,
  onItemsToggleAll?: (updatedItems: Array<MixedSelectDropdownItem>, data?: Data | null) => void,
  onItemsRemove?: (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ) => void,
  onItemsRemoveAll?: (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null
  ) => void
};

type State = {
  filteredItems: Array<MixedSelectDropdownItem>,
  selectedItems: Array<MixedSelectDropdownItem>,
  isMenuOpen: boolean,
  isMouseOverMenu: boolean,
  inputValue: string
};

const MSG_KEY = 'mixed_select_dropdown';

class MixedSelectDropdown extends React.PureComponent<Props, State> {
  state = {
    filteredItems: this.props.items,
    selectedItems: getSelectedItems(this.props.items),
    isMenuOpen: false,
    isMouseOverMenu: false,
    inputValue: ''
  };

  controlRef: React.RefObject<any> = React.createRef();
  inputFieldRef: TextEntry | undefined | null = null;

  componentDidMount() {
    window.addEventListener('scroll', this.hideMenuOnScroll);

    this.handleExceptions();
  }

  componentDidUpdate(prevProps: Props) {
    const { items } = this.props;
    const { selectedItems } = this.state;
    const updatedSelectedItems = getSelectedItems(items);
    const isStateSynched = isEqual(selectedItems, updatedSelectedItems);

    // !isStateSynched happens when selecting an item that is not allowed
    // e.g. YT task type is selected when only IG task types are allowed
    if (!isEqual(items, prevProps.items) || !isStateSynched) {
      // list was updated dynamically or selected items are out of sync with incoming list
      this.refreshState(!isStateSynched);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.hideMenuOnScroll);
  }

  hideMenuOnScroll = (event: WindowEventMap['scroll']): void => {
    const { isMenuOpen, isMouseOverMenu } = this.state;
    const target = event.target;

    if (isMenuOpen && !isMouseOverMenu && (target as any)?.className === undefined) {
      this.handleMenuToggle(false);
    }

    event.stopPropagation();
  };

  getItemsState(updatedItems: Array<MixedSelectDropdownItem>): MixedSelectDropdownItemState {
    const { withSavedInput } = this.props;
    const { inputValue } = this.state;

    return {
      selectedItems: getSelectedItems(updatedItems),
      filteredItems: this.handleFilterItems(updatedItems, inputValue),
      inputValue: withSavedInput ? inputValue : ''
    };
  }

  refreshState(isStateNotSynched: boolean) {
    const { items, withSearchable, withDynamicData } = this.props;
    const { inputValue } = this.state;

    if (withSearchable || withDynamicData || isStateNotSynched) {
      this.setState({
        filteredItems: this.handleFilterItems(items, inputValue),
        selectedItems: getSelectedItems(items)
      });
    }
  }

  showSelectAll(): boolean {
    const { items, withSelectAll } = this.props;
    const { inputValue } = this.state;
    const selectedItems = getSelectedItems(items);

    return Boolean(withSelectAll)
      && selectedItems.length !== items.length
      && isEmpty(inputValue);
  }

  handleExceptions = (): void => {
    const {
      withMulti,
      withCloseOnSelect,
      withSelectedInList,
      withTags
    } = this.props;

    const mixedSelectDropdownMsg = __t(`${MSG_KEY}.labels.mixed_select_dropdown`);
    const seeStorybookMsg = __t(`${MSG_KEY}.messages.see_storybook`);

    if (Boolean(withMulti) && Boolean(withSelectedInList)) {
      const msg = __t(`${MSG_KEY}.errors.with_multi_and_with_selected_in_list`);
      logError(`${mixedSelectDropdownMsg}: ${msg} ${seeStorybookMsg}`);
    }

    if (!withMulti && Boolean(withCloseOnSelect)) {
      const msg = __t(`${MSG_KEY}.errors.with_multi_and_with_close_on_select`);
      logError(`${mixedSelectDropdownMsg}: ${msg} ${seeStorybookMsg}`);
    }

    if (Boolean(withMulti) && Boolean(withTags)) {
      const msg = __t(`${MSG_KEY}.errors.with_multi_and_with_tags`);
      logError(`${mixedSelectDropdownMsg}: ${msg} ${seeStorybookMsg}`);
    }
  };

  handleMenuToggle = (override?: boolean): void => {
    this.setState(state => {
      const isMenuOpen = isBoolean(override) ? override : !state.isMenuOpen;

      return {
        isMenuOpen,
        inputValue: isMenuOpen ? state.inputValue : ''
      };
    });
  };

  handleMouseEnterMenu = (): void => {
    this.setState({
      isMouseOverMenu: true
    });
  };

  handleMouseLeaveMenu = (): void => {
    this.setState({
      isMouseOverMenu: false
    });
  };

  handleInputRefCallback = (ref: TextEntry): void => {
    this.inputFieldRef = ref;
  };

  handleInputBlur = (): void => {
    this.handleMenuToggle(false);
  };

  handleInputFocus = (): void => {
    if (this.inputFieldRef) {
      this.inputFieldRef.focus();
    }
  };

  handleInputChange = (event: React.SyntheticEvent<TextEntry>): void => {
    const { items, onItemsSearch } = this.props;
    const { isMenuOpen } = this.state;
    const target = event.currentTarget;
    const value = target.value || '';

    if (!isMenuOpen) {
      this.handleMenuToggle(true);
    }

    this.setState({
      filteredItems: this.handleFilterItems(items, value),
      inputValue: value
    });

    if (onItemsSearch) {
      onItemsSearch(value);
    }
  };

  handleFilterItems = (items: Array<MixedSelectDropdownItem>, inputValue: string): Array<MixedSelectDropdownItem> => {
    const { withSearchable } = this.props;

    return withSearchable ? filterItems(items, inputValue) : items;
  };

  handleItemToggle = (identifier: string, isChecked: boolean): void => {
    const { items } = this.props;

    const deselectedItems = deselectAllItems(items);
    const updatedItem = getUpdatedItem(deselectedItems, identifier);
    const updatedItems = updatePropValueByIdentifier(deselectedItems, updatedItem.identifier, 'isSelected', isChecked);
    const itemsState = this.getItemsState(updatedItems);

    this.setState(itemsState, () => {
      this.handleItemsToggleCallback(updatedItems, updatedItem);
      this.handleInputBlur();
    });
  };

  handleItemsToggle = (identifier: string, isChecked: boolean): void => {
    const { items, maxItems, withCloseOnSelect } = this.props;
    const { selectedItems } = this.state;

    // skip toggle if already reached limit of items
    const shouldSkipToggle = maxItems
      && selectedItems.length >= maxItems
      && isChecked;

    if (shouldSkipToggle) {
      return;
    }

    const updatedItem = getUpdatedItem(items, identifier);
    const updatedItems = updatePropValueByIdentifier(items, updatedItem.identifier, 'isSelected', isChecked);
    const itemsState = this.getItemsState(updatedItems);

    this.setState(itemsState, () => {
      if (withCloseOnSelect) {
        this.handleInputBlur();
      }

      this.handleItemsToggleCallback(updatedItems, updatedItem);
    });
  };

  handleItemsToggleCallback(
    updatedItems: Array<MixedSelectDropdownItem>,
    updatedItem: Partial<MixedSelectDropdownItem>
  ): void {
    const { data, onItemsToggle } = this.props;

    if (onItemsToggle) {
      onItemsToggle(updatedItems, data, updatedItem);
    }
  }

  handleItemsToggleAll = (): void => {
    const { items } = this.props;

    this.setState({
      selectedItems: selectAllItems(items)
    }, () => {
      this.handleItemsToggleAllCallback();
    });
  };

  handleItemsToggleAllCallback(): void {
    const { data, onItemsToggleAll } = this.props;
    const { selectedItems } = this.state;

    if (onItemsToggleAll) {
      onItemsToggleAll(selectedItems, data);
    }
  }

  handleItemsRemove = (identifier: string): void => {
    const { items } = this.props;
    const { inputValue } = this.state;

    this.setState(state => ({
      selectedItems: removeItemFromList(state.selectedItems, identifier)
    }), () => {
      const removedItem = getRemovedItem(items, identifier);
      const updatedItems = updatePropValueByIdentifier(items, removedItem.identifier, 'isSelected', false);

      this.setState({
        filteredItems: this.handleFilterItems(updatedItems, inputValue)
      });

      this.handleItemsRemoveCallback(updatedItems, removedItem);
    });
  };

  handleItemsRemoveCallback(
    updatedItems: Array<MixedSelectDropdownItem>,
    removedItem: Partial<MixedSelectDropdownItem>
  ): void {
    const { data, onItemsRemove } = this.props;

    if (onItemsRemove) {
      onItemsRemove(updatedItems, data, removedItem);
    }
  }

  handleItemsRemoveAll = (): void => {
    this.handleInputFocus();

    this.setState({
      selectedItems: [],
      filteredItems: [],
      inputValue: ''
    }, () => {
      this.handleItemsRemoveAllCallback();
    });
  };

  handleItemsRemoveAllCallback(): void {
    const { items, data, onItemsRemoveAll } = this.props;

    if (onItemsRemoveAll) {
      onItemsRemoveAll(deselectAllItems(items), data);
    }
  }

  renderControl(): React.ReactNode {
    const {
      withMulti,
      withSearchable,
      withRemovable,
      withSortable,
      withColors,
      withIcons,
      withTruncate,
      withRandomizedAutocomplete,
      withSmallSize,
      withTags,
      hideChevron,
      inputClassName,
      controlClassName,
      placeholder,
      hideSelectedItems,
      isDisabled,
      onItemsSearch
    } = this.props;

    const { selectedItems, isMenuOpen, inputValue } = this.state;
    const updatedItems = sortItems(selectedItems, withSortable);

    return (
      <div
        ref={this.controlRef}
        className="mixed-select-dropdown__control"
      >
        <MixedSelectDropdownControl
          selectedItems={updatedItems}
          hideSelectedItems={hideSelectedItems}
          isMenuOpen={isMenuOpen}
          withMulti={withMulti}
          withSearchable={withSearchable || Boolean(onItemsSearch)}
          withRemovable={withRemovable}
          withColors={withColors}
          withIcons={withIcons}
          withTruncate={withTruncate}
          withSmallSize={withSmallSize}
          withTags={withTags}
          withRandomizedAutocomplete={withRandomizedAutocomplete}
          hideChevron={hideChevron}
          inputValue={inputValue}
          inputClassName={inputClassName}
          controlClassName={controlClassName}
          placeholder={placeholder}
          isDisabled={isDisabled}
          onInputRefCallback={this.handleInputRefCallback}
          onInputChange={this.handleInputChange}
          onInputFocus={this.handleInputFocus}
          onInputBlur={this.handleInputBlur}
          onItemsRemove={this.handleItemsRemove}
          onItemsRemoveAll={this.handleItemsRemoveAll}
          onMenuToggle={this.handleMenuToggle}
        />
      </div>
    );
  }

  renderMenuContainer(): React.ReactNode {
    const {
      items,
      name,
      withMulti,
      withSortable,
      withCheckboxes,
      withColors,
      withIcons,
      withTruncate,
      withSearchable,
      withSelectedInList,
      withSmallSize,
      withTags,
      menuClassName,
      menuItemClassName,
      onItemsSearch
    } = this.props;

    const { filteredItems, isMenuOpen, inputValue } = this.state;
    const updatedFilteredItems = withCheckboxes ? filteredItems : getUnselectedItems(filteredItems);
    const keepSelectedItemInList = withCheckboxes || (withSelectedInList && !withMulti);

    let updatedItems = keepSelectedItemInList ? items : getUnselectedItems(items);
    updatedItems = !isEmpty(inputValue) ? updatedFilteredItems : updatedItems;
    updatedItems = sortItems(updatedItems, withSortable);

    return (
      <MixedSelectDropdownMenuContainer
        key={`mixed-select-dropdown__menu-container-${name}`}
        items={updatedItems}
        withMulti={withMulti}
        withCheckboxes={withCheckboxes}
        withColors={withColors}
        withIcons={withIcons}
        withTruncate={withTruncate}
        withSelectedInList={withSelectedInList}
        withSelectAll={this.showSelectAll()}
        withSmallSize={withSmallSize}
        withTags={withTags}
        controlRef={this.controlRef}
        isMenuOpen={isMenuOpen}
        inputValue={(withSearchable || Boolean(onItemsSearch)) ? inputValue : undefined}
        menuClassName={menuClassName}
        menuItemClassName={menuItemClassName}
        onItemToggle={this.handleItemToggle}
        onItemsToggle={this.handleItemsToggle}
        onItemsToggleAll={this.handleItemsToggleAll}
        onMouseEnterMenu={this.handleMouseEnterMenu}
        onMouseLeaveMenu={this.handleMouseLeaveMenu}
      />
    );
  }

  render(): React.ReactNode {
    const { className, data, withSmallSize } = this.props;

    const containerClasses = classnames('mixed-select-dropdown', className, {
      'mixed-select-dropdown--with-small-size': Boolean(withSmallSize)
    });

    return (
      <div
        className={containerClasses}
        {...expandDataProps(data)}
      >
        {this.renderControl()}
        {this.renderMenuContainer()}
      </div>
    );
  }
}

export default MixedSelectDropdown;
